import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import NAV_DATA, {
  FeatureFlagsProductType,
  PrimaryNavIdentifier,
  SecondaryNavItem,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { publishFeatureFlagOverrides } from '@zola-helpers/client/dist/es/featureFlags';
import PreauthNavSkeletonLoader from '@zola/zola-ui/src/components/PreauthNavSkeletonLoader';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';

import cx from 'classnames';
import { useRouter } from 'next/dist/client/router';
import Head from 'next/head';

import { useSearchedLocation } from '~/contexts/SearchContext';
import { FLAGS } from '~/util/featureFlags';

import initNavData from './util/navData';
import { getNavHeights, getNavSource, showSecondaryNav, showTertiaryNav } from './util/navUtils';
import getSecondaryNavItems, { getExploreVendorsDropdownItems } from './util/secondaryNavData';

import styles from './nav.module.less';

const {
  EVENTS: { NAV_LOADED },
} = NAV_DATA;

type NavProps = {
  navHtml: string | null;
  user?: UserContext | null;
  activePrimaryLink?: string;
  activeSecondaryLink?: string;
  activeTertiaryLink?: string;
  disablePrimaryNavCollapse?: boolean;
  disableSecondary?: boolean;
  hideBanner?: boolean;
  showInquiryJewel?: boolean;
  className?: string;
  ssr?: boolean;
  showSecondaryOnLoad?: boolean;
  isClpTest?: boolean;
};

const Nav: React.FC<NavProps> = ({
  navHtml,
  user = null,
  activePrimaryLink = PrimaryNavIdentifier.VENDORS,
  activeSecondaryLink,
  activeTertiaryLink,
  disablePrimaryNavCollapse = false,
  disableSecondary = false,
  hideBanner = false,
  showInquiryJewel = false,
  showSecondaryOnLoad = false,
  children,
  ssr = false,
  isClpTest,
}): JSX.Element => {
  const { navScript, navStyle } = getNavSource(navHtml);
  const isGuest = user ? Boolean(user.is_guest) : true;
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [showSecondary, setShowSecondary] = useState(showSecondaryOnLoad);
  const [showTertiary, setShowTertiary] = useState(false);
  const [secondaryData, setSecondaryData] = useState<SecondaryNavItem[] | undefined>(undefined);
  const [tertiaryData, setTertiaryData] = useState<SecondaryNavItem[] | undefined>(undefined);
  const { pathname } = useRouter();
  const { searchedLocation } = useSearchedLocation();
  const includedLocation = isClpTest && pathname === '/wedding-vendors' ? searchedLocation : null;

  useEffect(() => {
    setShowAnnouncement(!hideBanner);
  }, [hideBanner]);

  useEffect(() => {
    const newShowSecondary = !disableSecondary && showSecondaryNav(isGuest);
    setShowSecondary(newShowSecondary);
    setSecondaryData(
      newShowSecondary ? getSecondaryNavItems(user, showInquiryJewel, includedLocation) : undefined
    );
  }, [user, showInquiryJewel, disableSecondary, isGuest, includedLocation]);

  useEffect(() => {
    const newShowTertiary = showTertiaryNav(isGuest);
    setShowTertiary(newShowTertiary);
    setTertiaryData(newShowTertiary ? getExploreVendorsDropdownItems(includedLocation) : undefined);
  }, [isGuest, includedLocation]);

  const navHeights = useMemo(() => {
    return getNavHeights(isGuest, showAnnouncement, showSecondary, showTertiary);
  }, [isGuest, showAnnouncement, showSecondary, showTertiary]);

  const handleInitNavData = useCallback(() => {
    initNavData({
      userContext: user,
      secondaryData,
      tertiaryData,
      activePrimaryLink,
      activeSecondaryLink,
      activeTertiaryLink,
      disablePrimaryNavCollapse,
      hideBanner,
    });
    publishFeatureFlagOverrides(FLAGS, FeatureFlagsProductType.WEB_MARKETPLACE);
  }, [
    user,
    secondaryData,
    tertiaryData,
    activePrimaryLink,
    activeSecondaryLink,
    activeTertiaryLink,
    disablePrimaryNavCollapse,
    hideBanner,
  ]);

  useEffect(() => {
    window.addEventListener(NAV_LOADED, handleInitNavData);

    return () => {
      window.removeEventListener(NAV_LOADED, handleInitNavData);
    };
  }, [handleInitNavData]);

  useEffect(() => {
    handleInitNavData();
  }, [user, secondaryData, handleInitNavData]);

  // The DOM structure in the root-nav container should match what's in the index.html of web-nav
  // The key in the skeleton loader is necessary to prevent an error when replacing the nav HTML (see https://stackoverflow.com/a/54644168)
  return (
    <Fragment>
      {!ssr && (
        <Head>
          <link rel="stylesheet" href={navStyle} />
          <script async src={navScript} />
        </Head>
      )}
      <div className={cx(styles.navContainer, { [styles.postAuth]: !isGuest })}>
        {ssr ? (
          <div
            id="root-nav-container"
            data-testid="root-nav"
            dangerouslySetInnerHTML={{ __html: navHtml || '' }}
          >
            {children}
          </div>
        ) : (
          <Fragment>
            <div id="root-nav" data-testid="root-nav">
              <div key="PreauthNavSkeletonLoader">
                <PreauthNavSkeletonLoader
                  showPromoBar={showAnnouncement}
                  showSecondaryRow={!disableSecondary}
                />
              </div>
              {children}
            </div>
          </Fragment>
        )}
        <div id="notification-root" data-testid="notification-root" />
      </div>
      {process.env.NODE_ENV !== 'test' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .${styles.navContainer} {
              height: ${navHeights.mobile}px;
              ${MEDIA_QUERY.DESKTOP} {
                height: ${navHeights.desktop}px;
              }
            }
          `.replace(/\s+/g, ' '),
          }}
        />
      )}
    </Fragment>
  );
};

export default Nav;
